// V2 Articles library — spec 7b. Card-per-article layout with status pill,
// origin sub-line, and stat strip. Reuses the hostedPages backend with
// kind='article'. Stats are 0-filled in Phase 1 (analytics land in Phase 3).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBrain, IconDots, IconPlus, IconTrash } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { deleteArticle, fetchArticleAnalytics, fetchArticles } from '../services/api';

function classifyStatus(article) {
  const raw = String(article?.articleStatus || 'draft').toLowerCase();
  if (raw === 'promoting') return 'promoting';
  if (raw === 'published') return 'published';
  return 'draft';
}

function isExternalArticle(article) {
  return String(article?.kind || '').toLowerCase() === 'external';
}

function isContextArticle(article) {
  return String(article?.articleRole || '').toLowerCase() === 'context';
}

function statusDotColor(status) {
  if (status === 'promoting') return 'var(--mantine-color-sage-6)';
  if (status === 'published') return 'var(--mantine-color-sage-4)';
  return 'var(--aviary-disabled)';
}

function statusPillStyle(status) {
  const base = {
    padding: '2px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  };
  if (status === 'promoting') {
    return { ...base, background: 'var(--mantine-color-sage-1)', color: 'var(--mantine-color-sage-8)' };
  }
  if (status === 'published') {
    return { ...base, background: 'var(--mantine-color-sage-0, #eef4f0)', color: 'var(--mantine-color-sage-7)' };
  }
  return { ...base, background: 'var(--aviary-surface)', color: 'var(--aviary-muted)' };
}

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function StatCell({ label, value }) {
  return (
    <Stack gap={0} align="flex-start">
      <Text size="lg" fw={700} style={{ color: 'var(--aviary-ink)', lineHeight: 1.1 }}>
        {value}
      </Text>
      <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
        {label}
      </Text>
    </Stack>
  );
}

function ArticleCard({ article, onOpen, onDelete }) {
  const status = classifyStatus(article);
  const external = isExternalArticle(article);
  // Phase 3 will populate real stats; for now they're zeros so the layout is right.
  const stats = article.stats || { linksSent: 0, opened: 0, reactivated: 0, booked: 0 };
  return (
    <Paper
      p="lg"
      radius="md"
      withBorder
      style={{ background: '#fff', cursor: 'pointer' }}
      onClick={onOpen}
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Box
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: statusDotColor(status),
                  flexShrink: 0,
                }}
              />
              <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 18 }} truncate>
                {article.title || 'Untitled article'}
              </Text>
              <Text component="span" style={statusPillStyle(status)}>
                {status}
              </Text>
              {external && (
                <Text
                  component="span"
                  style={{
                    padding: '2px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    background: 'var(--aviary-surface)',
                    color: 'var(--aviary-muted)',
                  }}
                >
                  External
                </Text>
              )}
              {isContextArticle(article) && (
                <Text
                  component="span"
                  style={{
                    padding: '2px 10px',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    background: 'var(--mantine-color-blue-0)',
                    color: 'var(--mantine-color-blue-7)',
                  }}
                >
                  Context
                </Text>
              )}
            </Group>
            <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
              {article.originNote || 'Uploaded HTML'}
              {article.updatedAt ? ` · updated ${shortDate(article.updatedAt)}` : ''}
            </Text>
          </Stack>
          <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
            <Menu shadow="md" position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="sm" aria-label="Article options">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => onDelete?.(article)}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        <Group gap="xl">
          <StatCell label="links sent" value={stats.linksSent} />
          <StatCell label="opened" value={stats.opened} />
          <StatCell label="reactivated" value={stats.reactivated} />
          <StatCell label="booked" value={stats.booked} />
        </Group>
      </Stack>
    </Paper>
  );
}

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'draft', label: 'Draft' },
];

export default function AviaryV2ArticlesPage() {
  const api = useApi();
  const navigate = useNavigate();

  const [articles, setArticles] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadWithStats() {
      try {
        const fetched = await fetchArticles(api);
        // Context articles live in Knowledge Base, not here.
        const list = fetched.filter((a) => !isContextArticle(a));
        if (cancelled) return;
        // Show cards immediately with zero-stubs, then enrich in the background.
        setArticles(list);
        // N+1 fetch — fine at current volume (jackie has < 10 articles). If the
        // library grows, replace with a batched /analytics-summary endpoint
        // (see Backlog V2.9 Phase 5).
        const enriched = await Promise.all(
          list.map(async (a) => {
            try {
              const analytics = await fetchArticleAnalytics(api, a.slug);
              return { ...a, stats: analytics?.stats || null };
            } catch (_err) {
              return a;
            }
          })
        );
        if (!cancelled) setArticles(enriched);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      }
    }
    loadWithStats();
    return () => { cancelled = true; };
  }, [api]);

  const byStatus = useMemo(() => {
    const list = articles || [];
    const map = {
      all: list,
      published: [],
      draft: [],
    };
    for (const a of list) {
      const status = classifyStatus(a);
      // 'promoting' counts as published for tab purposes (it's live + being promoted).
      if (status === 'published' || status === 'promoting') map.published.push(a);
      else map.draft.push(a);
    }
    return map;
  }, [articles]);

  const visibleList = byStatus[filter] || byStatus.all;

  async function handleConfirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteArticle(api, deleteTarget.slug);
      setArticles((prev) => (prev || []).filter((a) => a.slug !== deleteTarget.slug));
      notifications.show({
        title: 'Article deleted',
        message: `"${deleteTarget.title || 'Article'}" removed.`,
        color: 'sage',
      });
      setDeleteTarget(null);
    } catch (err) {
      notifications.show({
        title: 'Could not delete article',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load articles</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }

  if (articles === null) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  return (
    <Box style={{ padding: '40px 24px 120px', maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Articles
          </Text>
          <Group gap="sm">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconBrain size={16} />}
              onClick={() => navigate('/v2/knowledge-base')}
            >
              Knowledge Base
            </Button>
            <Button
              color="sage"
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/v2/articles/new')}
            >
              Add new
            </Button>
          </Group>
        </Group>

        <Group gap="xs">
          {FILTER_TABS.map((tab) => {
            const count = (byStatus[tab.key] || []).length;
            const active = filter === tab.key;
            return (
              <UnstyledButton
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  border: `1px solid ${active ? 'var(--aviary-ink)' : 'var(--aviary-disabled)'}`,
                  background: active ? 'var(--aviary-ink)' : 'transparent',
                  color: active ? '#fff' : 'var(--aviary-ink)',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {tab.label} {count}
              </UnstyledButton>
            );
          })}
        </Group>

        {visibleList.length === 0 ? (
          <Paper p="xl" radius="md" withBorder style={{ background: 'var(--aviary-surface)' }}>
            <Stack gap="sm" align="center">
              <Text style={{ color: 'var(--aviary-muted)' }}>
                {filter === 'all' ? 'No articles yet.' : `No ${filter} articles.`}
              </Text>
              {filter === 'all' && (
                <Button
                  color="sage"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => navigate('/v2/articles/new')}
                >
                  Add your first article
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Stack gap="md">
            {visibleList.map((article) => (
              <ArticleCard
                key={article.slug}
                article={article}
                onOpen={() => navigate(`/v2/articles/${article.slug}`, {
                  state: { back: { path: '/v2/articles', label: 'Articles' } },
                })}
                onDelete={(a) => setDeleteTarget(a)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      <Modal
        opened={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title="Delete this article?"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>{deleteTarget?.title || 'This article'}</strong> and its magic-link history will be removed.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirmDelete} loading={deleting}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
